import { useState, useEffect, useCallback } from "react";
import { useSession } from "../lib/auth-client";
import { useLocation, useNavigate } from "react-router-dom";
import { apiFetch } from "../api";
import {
  Shield, X, Sparkles, Activity, Trash2, RotateCcw, Bug,
  ExternalLink, Database, Loader2, ChevronUp, ChevronDown,
  Play, Eye, Clock, Server, Wand2, PenLine
} from "lucide-react";

export default function AdminToolbar() {
  const { data: session } = useSession();
  const isSignedIn = !!session;
  const location = useLocation();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState<string | null>(null);
  const [toast, setToast] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [envInfo, setEnvInfo] = useState<any>(null);
  const [quickTopic, setQuickTopic] = useState("");

  // Check admin status once on mount
  useEffect(() => {
    if (!isSignedIn) return;
    (async () => {
      try {
        await apiFetch("/admin/stats");
        setIsAdmin(true);
      } catch {
        setIsAdmin(false);
      }
    })();
  }, [isSignedIn]);

  const adminAction = useCallback(async (action: string, body?: any) => {
    setLoading(action);
    setToast(null);
    try {
      const result = await apiFetch(`/admin/actions/${action}`, {
        method: "POST",
        body: body ? JSON.stringify(body) : "{}",
      });
      const modeInfo = result.mode ? ` (${result.mode})` : "";
      setToast({ type: "success", text: `${action}${modeInfo}` });
    } catch (e: any) {
      setToast({ type: "error", text: e.message });
    } finally {
      setLoading(null);
    }
  }, []);

  const fetchEnvInfo = useCallback(async () => {
    setLoading("env-info");
    try {
      const stats = await apiFetch("/admin/stats");
      setEnvInfo(stats);
    } catch {
    } finally {
      setLoading(null);
    }
  }, []);

  // Auto-dismiss toast
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  if (!isAdmin) return null;

  // Extract report ID from URL if on a report page
  const reportMatch = location.pathname.match(/^\/report\/(.+)$/);
  const reportId = reportMatch?.[1];

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50">
      {/* Toast */}
      {toast && (
        <div className={`absolute -top-10 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium shadow-lg ${
          toast.type === "error" ? "bg-red-600 text-white" : "bg-green-600 text-white"
        }`}>
          {toast.text}
        </div>
      )}

      {/* Expanded panel */}
      {expanded && (
        <div className="mb-2 bg-zinc-900 border border-zinc-700 rounded-lg shadow-2xl p-3 min-w-[420px] text-white">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Admin Tools</span>
            <button onClick={() => setExpanded(false)} className="text-zinc-500 hover:text-white">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* Quick actions grid */}
          <div className="grid grid-cols-2 gap-1.5 mb-3">
            <ToolbarButton
              icon={<Sparkles className="h-3.5 w-3.5" />}
              label="AI Publish"
              loading={loading === "run-auto-publish"}
              onClick={() => adminAction("run-auto-publish")}
            />
            <ToolbarButton
              icon={<Activity className="h-3.5 w-3.5" />}
              label="Run Monitor"
              loading={loading === "run-monitor"}
              onClick={() => adminAction("run-monitor")}
            />
            <ToolbarButton
              icon={<Trash2 className="h-3.5 w-3.5" />}
              label="Clear Cache"
              loading={loading === "clear-cache"}
              onClick={() => adminAction("clear-cache")}
            />
            <ToolbarButton
              icon={<Server className="h-3.5 w-3.5" />}
              label="Stats"
              loading={loading === "env-info"}
              onClick={() => { fetchEnvInfo(); }}
            />
          </div>

          {/* Blog quick generate */}
          <div className="border-t border-zinc-700 pt-2 mb-2">
            <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1.5">Quick Blog</p>
            <div className="flex gap-1.5 mb-1.5">
              <input
                type="text"
                placeholder="Topic (empty = auto)"
                value={quickTopic}
                onChange={(e) => setQuickTopic(e.target.value)}
                className="flex-1 bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-[11px] text-zinc-300 placeholder:text-zinc-600 focus:outline-none focus:border-zinc-500"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    adminAction("run-auto-publish", quickTopic ? { topic: quickTopic } : undefined);
                    setQuickTopic("");
                  }
                }}
              />
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              <ToolbarButton
                icon={<Wand2 className="h-3.5 w-3.5" />}
                label="Generate Draft"
                loading={loading === "run-auto-publish"}
                onClick={() => {
                  adminAction("run-auto-publish", {
                    topic: quickTopic || undefined,
                    publishImmediately: false,
                  });
                  setQuickTopic("");
                }}
              />
              <ToolbarButton
                icon={<Sparkles className="h-3.5 w-3.5" />}
                label="Generate & Publish"
                loading={loading === "run-auto-publish"}
                onClick={() => {
                  adminAction("run-auto-publish", {
                    topic: quickTopic || undefined,
                    publishImmediately: true,
                  });
                  setQuickTopic("");
                }}
              />
            </div>
          </div>

          {/* Context-aware: Report page actions */}
          {reportId && (
            <div className="border-t border-zinc-700 pt-2 mb-2">
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1.5">Report: {reportId.slice(0, 8)}...</p>
              <div className="grid grid-cols-3 gap-1.5">
                <ToolbarButton
                  icon={<Play className="h-3.5 w-3.5" />}
                  label="Restart"
                  loading={loading === "restart-job"}
                  onClick={() => adminAction("restart-job", { jobId: reportId })}
                  small
                />
                <ToolbarButton
                  icon={<RotateCcw className="h-3.5 w-3.5" />}
                  label="Retry Fresh"
                  loading={loading === "retry-job"}
                  onClick={() => adminAction("retry-job", { jobId: reportId })}
                  small
                />
                <ToolbarButton
                  icon={<X className="h-3.5 w-3.5" />}
                  label="Cancel"
                  loading={loading === "cancel-job"}
                  onClick={() => adminAction("cancel-job", { jobId: reportId })}
                  small
                />
              </div>
            </div>
          )}

          {/* Stats display */}
          {envInfo && (
            <div className="border-t border-zinc-700 pt-2 mb-2">
              <div className="grid grid-cols-4 gap-2 text-center">
                <div><p className="text-lg font-bold">{envInfo.totalJobs}</p><p className="text-[10px] text-zinc-500">Jobs</p></div>
                <div><p className="text-lg font-bold">{envInfo.activeJobs}</p><p className="text-[10px] text-zinc-500">Active</p></div>
                <div><p className="text-lg font-bold">{envInfo.totalUsers}</p><p className="text-[10px] text-zinc-500">Users</p></div>
                <div><p className="text-lg font-bold text-red-400">{envInfo.recentErrors}</p><p className="text-[10px] text-zinc-500">Errors 24h</p></div>
              </div>
            </div>
          )}

          {/* Quick nav */}
          <div className="border-t border-zinc-700 pt-2 flex gap-1.5">
            <button onClick={() => navigate("/admin")} className="text-[11px] text-zinc-400 hover:text-white px-2 py-1 rounded hover:bg-zinc-800 transition-colors flex items-center gap-1">
              <Shield className="h-3 w-3" /> Admin
            </button>
            <button onClick={() => navigate("/blog")} className="text-[11px] text-zinc-400 hover:text-white px-2 py-1 rounded hover:bg-zinc-800 transition-colors flex items-center gap-1">
              <Eye className="h-3 w-3" /> Blog
            </button>
            <button onClick={() => navigate("/admin?tab=blog")} className="text-[11px] text-zinc-400 hover:text-white px-2 py-1 rounded hover:bg-zinc-800 transition-colors flex items-center gap-1">
              <PenLine className="h-3 w-3" /> Posts
            </button>
            <button onClick={() => navigate("/sitemap.xml")} className="text-[11px] text-zinc-400 hover:text-white px-2 py-1 rounded hover:bg-zinc-800 transition-colors flex items-center gap-1">
              <Database className="h-3 w-3" /> Sitemap
            </button>
            <a href="https://dash.cloudflare.com" target="_blank" rel="noopener noreferrer" className="text-[11px] text-zinc-400 hover:text-white px-2 py-1 rounded hover:bg-zinc-800 transition-colors flex items-center gap-1">
              <ExternalLink className="h-3 w-3" /> CF Dash
            </a>
          </div>

          {/* Debug info */}
          <div className="border-t border-zinc-700 pt-2 mt-2">
            <p className="text-[10px] text-zinc-600 font-mono">
              {location.pathname} | {new Date().toLocaleTimeString()}
            </p>
          </div>
        </div>
      )}

      {/* Collapsed pill */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-white rounded-full px-4 py-2 shadow-2xl transition-all text-sm font-medium"
      >
        <Shield className="h-4 w-4 text-green-400" />
        <span className="text-xs">Admin</span>
        {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />}
      </button>
    </div>
  );
}

function ToolbarButton({
  icon, label, loading, onClick, small,
}: {
  icon: React.ReactNode; label: string; loading: boolean; onClick: () => void; small?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className={`flex items-center gap-1.5 rounded-md bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-300 hover:text-white transition-colors disabled:opacity-50 ${
        small ? "px-2 py-1 text-[10px]" : "px-2.5 py-1.5 text-[11px]"
      }`}
    >
      {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : icon}
      {label}
    </button>
  );
}
